'use client'

import { format, formatDistanceToNowStrict } from 'date-fns'
import { motion } from 'framer-motion'
import { CrownIcon, Loader2Icon, MailIcon, SendIcon, UserMinusIcon, XIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import { useState } from 'react'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import {
  accountRoleTranslationKey,
  ASSIGNABLE_ROLES,
  isAccountOwner,
} from '@/features/account-access/lib/roles'
import { useAuth } from '@/features/auth/hooks'
import { useCapabilities } from '@/hooks/use-capabilities'
import { BILLING_ROUTE } from '@/lib/billing/routes'
import { toast } from '@/lib/toast'
import { cn } from '@/lib/utils'
import {
  useAccountAccess,
  useChangeAccountRole,
  useInviteToAccount,
  useRemoveAccountAccess,
  useRevokeInvite,
  useTransferAccountOwnership,
} from '@/service/account-access.hook'
import { AccountRole, PublicAccount } from '@/types/generated/common/account'
import { AccountMemberEntry } from '@/types/generated/public_api'

export function ManageAccessPanel({ account }: { account: PublicAccount }) {
  const t = useTranslations('settings')
  const tBilling = useTranslations('billing')
  const { user } = useAuth()
  const { maxSharedMembers } = useCapabilities()
  const { data, isLoading } = useAccountAccess(account.accountId)
  const [removeTarget, setRemoveTarget] = useState<AccountMemberEntry | null>(null)
  const [transferTarget, setTransferTarget] = useState<AccountMemberEntry | null>(null)

  // The caller's own role on this account. Mutation controls (invite, role,
  // remove, revoke, transfer) are owner-only; the member list is visible to
  // every member, so non-owners get a read-only roster.
  const isOwner = isAccountOwner(account.role)
  const members = data?.members ?? []
  const pendingInvites = data?.pendingInvites ?? []

  const { mutate: changeRole, isPending: isChangingRole } = useChangeAccountRole({
    onSuccess: (result) => toast.success(result.message),
    onError: (error) => toast.error(error.message || t('failedUpdateRole')),
  })

  const { mutateAsync: removeAccess, isPending: isRemoving } = useRemoveAccountAccess({
    onSuccess: (result) => {
      toast.success(result.message)
      setRemoveTarget(null)
    },
    onError: (error) => {
      toast.error(error.message || t('failedRemoveAccess'))
      setRemoveTarget(null)
    },
  })

  const { mutate: revokeInvite, isPending: isRevoking } = useRevokeInvite({
    onSuccess: (result) => toast.success(result.message),
    onError: (error) => toast.error(error.message || t('failedRevokeInvite')),
  })

  const { mutateAsync: transferOwnership, isPending: isTransferring } = useTransferAccountOwnership(
    {
      onSuccess: (result) => {
        toast.success(result.message)
        setTransferTarget(null)
      },
      onError: (error) => {
        toast.error(error.message || t('failedTransferOwnership'))
        setTransferTarget(null)
      },
    }
  )

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="flex w-full flex-col gap-4"
    >
      <Card className="w-full bg-white/2 p-6">
        <CardHeader className="border-b-0 p-0 pb-4 font-sans text-base font-medium tracking-tight text-white/60">
          {isOwner ? t('manageAccess') : t('members')}
        </CardHeader>
        <CardContent className="flex w-full flex-col gap-6 p-0">
          {/* Inviting is owner-only. Sharing is a paid capability, so show an
              upgrade affordance instead of silently hiding the form when the
              plan allows no extra members. */}
          {isOwner &&
            (maxSharedMembers > 0 ? (
              <InviteForm accountId={account.accountId} />
            ) : (
              <div className="flex flex-col items-start gap-2 border border-white/10 bg-white/[0.02] p-4">
                <p className="text-foreground/60 text-xs">{tBilling('sharingUpgrade')}</p>
                <Button asChild variant="basic" size="sm">
                  <Link href={BILLING_ROUTE}>{tBilling('upgrade')}</Link>
                </Button>
              </div>
            ))}

          {isLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2Icon className="size-4 animate-spin text-white/40" />
            </div>
          ) : (
            <>
              {/* Members */}
              <div className="flex flex-col gap-2">
                <p className="text-xs font-medium tracking-tight text-white/40 uppercase">
                  {t('members')}
                </p>
                {members.map((member) => {
                  const isMemberOwner = isAccountOwner(member.role)
                  const isSelf = member.userId === user?.userId
                  // Owner-only controls, and never on the protected owner row —
                  // the owner can't be removed or demoted (only transferred).
                  const canManageMember = isOwner && !isMemberOwner
                  return (
                    <div
                      key={member.userId}
                      className="flex items-center justify-between gap-3 bg-white/3 px-3 py-2"
                    >
                      <div className="flex min-w-0 flex-col">
                        <p className="truncate text-sm text-white">
                          {member.email || t('unknownUser')}
                          {isSelf && <span className="ml-1.5 text-white/40">({t('you')})</span>}
                        </p>
                        <p className="text-xs text-white/40">
                          {t('memberSince', {
                            date: format(Number(member.createdAt), 'MMM d, yyyy'),
                          })}
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        {canManageMember ? (
                          <>
                            <Select
                              value={String(member.role)}
                              disabled={isChangingRole}
                              onValueChange={(value) =>
                                changeRole({
                                  accountId: account.accountId,
                                  userId: member.userId,
                                  role: Number(value) as AccountRole,
                                })
                              }
                            >
                              <SelectTrigger size="sm" className="w-32">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {ASSIGNABLE_ROLES.map((role) => (
                                  <SelectItem key={role} value={String(role)} className="text-xs">
                                    {t(`role.${accountRoleTranslationKey(role)}`)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  disabled={isTransferring}
                                  onClick={() => setTransferTarget(member)}
                                  className={cn(
                                    'flex size-7 items-center justify-center border border-white/20',
                                    isTransferring && 'cursor-not-allowed opacity-40'
                                  )}
                                >
                                  <CrownIcon className="size-3 text-white" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{t('makeOwner')}</p>
                              </TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  disabled={isRemoving}
                                  onClick={() => setRemoveTarget(member)}
                                  className={cn(
                                    'flex size-7 items-center justify-center border border-white/20',
                                    isRemoving && 'cursor-not-allowed opacity-40'
                                  )}
                                >
                                  <UserMinusIcon className="size-3 text-white" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{t('removeMember')}</p>
                              </TooltipContent>
                            </Tooltip>
                          </>
                        ) : (
                          <Badge variant="outline" className="text-xs text-white/60">
                            {t(`role.${accountRoleTranslationKey(member.role)}`)}
                          </Badge>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>

              {/* Pending invites (owner-only management view) */}
              {isOwner && pendingInvites.length > 0 && (
                <div className="flex flex-col gap-2">
                  <p className="text-xs font-medium tracking-tight text-white/40 uppercase">
                    {t('pendingInvites')}
                  </p>
                  {pendingInvites.map((invite) => (
                    <div
                      key={invite.email}
                      className="flex items-center justify-between gap-3 bg-white/3 px-3 py-2"
                    >
                      <div className="flex min-w-0 items-center gap-2">
                        <MailIcon className="size-3.5 shrink-0 text-white/40" />
                        <div className="flex min-w-0 flex-col">
                          <p className="truncate text-sm text-white">{invite.email}</p>
                          <p className="text-xs text-white/40">
                            {t('invitedByExpires', {
                              email: invite.inviterEmail || t('unknown'),
                              time: formatDistanceToNowStrict(Number(invite.expiresAt), {
                                addSuffix: true,
                              }),
                            })}
                          </p>
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <Badge variant="outline" className="text-xs text-white/60">
                          {t(`role.${accountRoleTranslationKey(invite.role)}`)}
                        </Badge>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              aria-label={t('revokeInvite')}
                              variant="ghost"
                              size="icon"
                              type="button"
                              disabled={isRevoking}
                              onClick={() =>
                                revokeInvite({
                                  accountId: account.accountId,
                                  email: invite.email,
                                })
                              }
                              className={cn(
                                'flex size-7 items-center justify-center border border-white/20',
                                isRevoking && 'cursor-not-allowed opacity-40'
                              )}
                            >
                              <XIcon className="size-3 text-white" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{t('revokeInvite')}</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Remove member confirmation */}
      <AlertDialog
        open={Boolean(removeTarget)}
        onOpenChange={(open) => !open && setRemoveTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('removeMember')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('removeMemberDescription', {
                email: removeTarget?.email || t('thisMember'),
                name: account.name,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRemoving}>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={isRemoving}
              onClick={async () => {
                if (!removeTarget) return
                await removeAccess({
                  accountId: account.accountId,
                  userId: removeTarget.userId,
                })
              }}
            >
              {isRemoving ? t('removing') : t('remove')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Transfer ownership confirmation */}
      <AlertDialog
        open={Boolean(transferTarget)}
        onOpenChange={(open) => !open && setTransferTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('transferOwnershipTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('transferOwnershipDescription', {
                email: transferTarget?.email || t('thisMember'),
                name: account.name,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isTransferring}>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              disabled={isTransferring}
              onClick={async () => {
                if (!transferTarget) return
                await transferOwnership({
                  accountId: account.accountId,
                  targetUserId: transferTarget.userId,
                })
              }}
            >
              {isTransferring ? t('transferring') : t('transfer')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  )
}

function InviteForm({ accountId }: { accountId: string }) {
  const t = useTranslations('settings')
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<AccountRole>(AccountRole.ACCOUNT_ROLE_READ_ONLY)

  const { mutate: invite, isPending } = useInviteToAccount({
    onSuccess: (result) => {
      toast.success(result.message)
      setEmail('')
    },
    onError: (error) => toast.error(error.message || t('failedSendInvite')),
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = email.trim()
    if (!trimmed) return
    invite({ accountId, email: trimmed, role })
  }

  return (
    <form onSubmit={handleSubmit} className="flex items-center gap-2">
      <Input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder={t('inviteByEmail')}
        className="flex-1"
        inputClassName="h-9 px-3 py-0 text-sm"
      />
      <Select value={String(role)} onValueChange={(value) => setRole(Number(value) as AccountRole)}>
        <SelectTrigger className="h-9 w-32 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {ASSIGNABLE_ROLES.map((assignableRole) => (
            <SelectItem key={assignableRole} value={String(assignableRole)} className="text-xs">
              {t(`role.${accountRoleTranslationKey(assignableRole)}`)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <button
        type="submit"
        disabled={isPending || !email.trim()}
        className={cn(
          'flex h-9 items-center gap-1.5 border border-white/20 px-3 text-xs text-white',
          'hover:bg-white/10',
          (isPending || !email.trim()) && 'cursor-not-allowed opacity-40'
        )}
      >
        {isPending ? (
          <Loader2Icon className="size-3 animate-spin" />
        ) : (
          <SendIcon className="size-3" />
        )}
        {t('invite')}
      </button>
    </form>
  )
}
